import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

import { updateUserAddress } from '../../utils/service';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

const AddressesContext = createContext();

export const ADDRESSES_TABS = {
  LIST: 'LIST',
  EDIT: 'EDIT',
};

export const useAddressesContext = () => {
  const context = useContext(AddressesContext);
  if (!context) {
    throw new Error('useAddressesContext must be used within an AddressesProvider');
  }
  return context;
};

export const AddressesProvider = ({
  children,
  addresses = [],
  setAddresses,
  selectedAddress = null,
  onAddressSelect,
  onAddressSave,
  onBack,
  showContinueButton = false,
  onContinue,
  continueButtonText = 'Continue',
  selectionEnabled = false,
  addressFormSubmitButtonText = 'SAVE DETAILS',
  newAddressFormTitle = 'Add Address',
  designSystemForms = false,
  stayOnAddressFormAfterSave = false,
  onValidAddressChange,
  hideAddressFormBackLink = false,
}) => {
  const { userData, isSignedIn } = useFirebaseAuth();

  const [currentTab, setCurrentTab] = useState(
    addresses?.length > 0 ? ADDRESSES_TABS.LIST : ADDRESSES_TABS.EDIT,
  );
  const [editingAddress, setEditingAddress] = useState(null);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);

  const navigateToList = useCallback(() => {
    setCurrentTab(ADDRESSES_TABS.LIST);
    setEditingAddress(null);
  }, []);

  const navigateToEdit = useCallback((address) => {
    setCurrentTab(ADDRESSES_TABS.EDIT);
    setEditingAddress(address);
  }, []);

  const handleEditAddress = useCallback((address) => {
    navigateToEdit(address);
  }, [navigateToEdit]);

  const handleAddAddress = useCallback(() => {
    navigateToEdit(null);
  }, [navigateToEdit]);

  const handleSaveAddress = useCallback(async (addressData) => {
    const addressToSave = {
      ...addressData,
      // If editing existing address, keep its current priority and uid
      // If creating new address, don't assign specific priority (let backend handle it)
      priority: editingAddress?.priority || undefined,
      ...(editingAddress?.uid && { uid: editingAddress.uid }),
    };

    if (onAddressSave) {
      await onAddressSave(addressToSave);
    }

    if (onContinue && !isSignedIn) {
      onContinue();
      return;
    }

    if (stayOnAddressFormAfterSave) {
      return;
    }

    navigateToList();
  }, [
    editingAddress,
    onAddressSave,
    navigateToList,
    isSignedIn,
    onContinue,
    stayOnAddressFormAfterSave,
  ]);

  const handleSetPrimaryAddress = useCallback(async (address) => {
    if (!address?.uid) return;

    setIsUpdatingPriority(true);

    const updatedAddress = {
      ...address,
      priority: 1,
    };

    const { error } = await updateUserAddress({
      addressUid: address.uid,
      fromAddress: updatedAddress,
    });

    if (!error) {
      const userAddresses = addresses.map(curAddress => (curAddress.uid === updatedAddress.uid
        ? updatedAddress
        : { ...curAddress, priority: null }
      ));
      setAddresses(userAddresses);
    }

    setIsUpdatingPriority(false);
  }, [setAddresses, addresses]);

  const contextValue = useMemo(() => ({
    addresses,
    selectedAddress,
    currentTab,
    editingAddress,
    isUpdatingPriority,

    navigateToList,
    navigateToEdit,

    onAddressSelect,
    onBack,
    handleEditAddress,
    handleAddAddress,
    handleSaveAddress,
    handleSetPrimaryAddress,

    showContinueButton,
    onContinue,
    continueButtonText,
    selectionEnabled,
    addressFormSubmitButtonText,
    newAddressFormTitle,
    designSystemForms,
    isSignedIn,
    userData,
    onValidAddressChange,
    hideAddressFormBackLink,
  }), [
    addresses,
    selectedAddress,
    currentTab,
    editingAddress,
    isUpdatingPriority,
    navigateToList,
    navigateToEdit,
    onAddressSelect,
    onBack,
    handleEditAddress,
    handleAddAddress,
    handleSaveAddress,
    handleSetPrimaryAddress,
    showContinueButton,
    onContinue,
    continueButtonText,
    selectionEnabled,
    addressFormSubmitButtonText,
    newAddressFormTitle,
    designSystemForms,
    isSignedIn,
    userData,
    onValidAddressChange,
    hideAddressFormBackLink,
  ]);

  return (
    <AddressesContext.Provider value={contextValue}>
      {children}
    </AddressesContext.Provider>
  );
};

export default AddressesContext;
